import * as yup from 'yup';
import { CampaignFormValues } from '../../types';
import { buildDefaults } from '../shared/campaignDefaults';
import {
  hasBaseErrors,
  hasCaptureErrors,
  hasInvalidFlyerMessages,
  hasInvalidScheduledDelay,
  hasMissingGroups,
  hasMissingScheduledTime,
  hasScheduleOrderErrors,
} from '../shared/primaryActionChecks';
import { commonSchemaShape, withMessageOrderTest } from '../shared/validationSchema';
import { CampaignTypeSpec } from '../policy';
import { policyIssue } from '../issues';
import { isFlyer, standardFields, withCampaignDateSchedule } from './shared';

export const messageSpec: CampaignTypeSpec = {
  externalType: 'custom',
  externalTypeFor: config => (isFlyer(config) ? 'flyer' : 'custom'),
  scheduleKind: config => (isFlyer(config) ? 'flyer' : 'generic'),
  fields: standardFields,

  defaults: config => buildDefaults(config, {
    scheduledType: 'immediate',
    fallbackType: isFlyer(config) ? 'mms' : 'sms',
  }),

  fromCampaign: (base, campaign, config) => {
    if (isFlyer(config) || !campaign.dateScheduled || !base.scheduledMessages.length) return base;
    const [first, ...rest] = base.scheduledMessages;
    return {
      ...base,
      scheduledMessages: [
        { ...first, scheduledType: 'time', time: campaign.dateScheduled },
        ...rest,
      ],
    };
  },

  issues: (values, config) => policyIssue(
    hasBaseErrors(values, config, { skipFirstMessageText: isFlyer(config) })
    || hasCaptureErrors(values)
    || hasInvalidScheduledDelay(values)
    || hasMissingScheduledTime(values)
    || (isFlyer(config) && hasInvalidFlyerMessages(values))
    || hasMissingGroups(values)
    || hasScheduleOrderErrors(values),
    'message-primary-blocked',
  ),

  keepsEmptyMessage: (message, config) => isFlyer(config) && message.position === 1,

  payload: ({ base, values, config }) => {
    const { dateScheduled, scheduledMessages } = withCampaignDateSchedule(base.scheduledMessages);
    return {
      ...base,
      groups: values.groupIds ?? [],
      scheduledMessages: isFlyer(config)
        ? scheduledMessages.map(message => ({ ...message, delay: null }))
        : scheduledMessages,
      ...(dateScheduled ? { dateScheduled } : {}),
    };
  },

  schema: config => {
    const schema = withMessageOrderTest(
      yup.object(commonSchemaShape(config, { messages: true, groups: true, keywords: true })),
    );
    if (!isFlyer(config)) return schema;
    return schema.test(
      'flyer-mms-with-link',
      'Flyer campaign requires the first message to be MMS with at least one link',
      values => {
        const first = (values as CampaignFormValues).scheduledMessages?.[0];
        if (!first) return true;
        return first.type === 'mms' && Array.isArray(first.links) && first.links.length >= 1;
      },
    );
  },
};
